import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { SaveOperationHistoryEntry } from '../models/save-operation-history.types';
import { environment } from '@/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserHistoryService {
  private http = inject(HttpClient);
  private readonly backendUrl = environment.backendHost; // Get the API URL from the environment

  private readonly historyApiUrl = `${this.backendUrl}/api/v1/document/user/save-history`;

  // Signals for state management
  private historyEntriesSignal = signal<SaveOperationHistoryEntry[]>([]);
  private isLoadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public signals (read-only)
  public readonly historyEntries = this.historyEntriesSignal.asReadonly();
  public readonly isLoading = this.isLoadingSignal.asReadonly();
  public readonly error = this.errorSignal.asReadonly();

  constructor() { }

  fetchHistory(): void {
    this.isLoadingSignal.set(true);
    this.errorSignal.set(null);

    this.http.get<SaveOperationHistoryEntry[]>(this.historyApiUrl).pipe(
      tap(data => {
        this.historyEntriesSignal.set(data);
        this.isLoadingSignal.set(false);
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('Error fetching user history:', err);
        this.errorSignal.set('Failed to load history. Please try again later.');
        this.isLoadingSignal.set(false);
        this.historyEntriesSignal.set([]); // Clear data on error
        return of(null); // Return a safe observable to complete the stream
      })
    ).subscribe();
  }

  addOptimisticSaveEntry(documentId: string, documentName: string): void {
    const optimisticEntry: SaveOperationHistoryEntry = {
      documentId: documentId, // Could use a temporary unique ID if backend IDs are not known yet
      documentName: documentName,
      status: 'PENDING', // Special status for optimistic entry
      timestamp: Date.now() // Current time for immediate display
    };

    // Add to the beginning of the list and limit to 10 if you want to keep the list size consistent immediately
    this.historyEntriesSignal.update(entries => {
      const newEntries = [optimisticEntry, ...entries];
      // Optionally, keep the list to a certain size, e.g., 10, like the backend does.
      // However, this optimistic entry is short-lived.
      // return newEntries.slice(0, 10);
      return newEntries;
    });
    // No need to set isLoading or error for this optimistic update.
  }

  /**
   * Reset all service state (useful for logout/user switching)
   */
  reset(): void {
    this.historyEntriesSignal.set([]);
    this.isLoadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
